import type { Locale, ProductCatalogItem } from "@/catalog";
import {
  isValidationPlaybook,
  type ValidationPlaybookStep,
} from "@/validation-playbook";

export interface OpportunityAnalysisPayload {
  verdict: {
    decision: "build" | "adapt" | "do_not_build_yet";
    confidence: "high" | "medium" | "low";
    summary: string;
    reasons: string[];
  };
  problem: string;
  whyNow: string;
  wedge: string;
  coreLoop: string;
  smallestTest: string;
  killCriterion: string;
  nonGoals: string[];
  historicalContext: string;
  historicalOutcome: {
    disposition: string;
    dispositionConfidence: "confirmed" | "reported" | "inferred" | "unknown";
    causalStatus: "confirmed" | "reported" | "inferred" | "not_publicly_established" | "not_applicable";
    summary: string;
    factors: string[];
    sourceUrls: string[];
  };
  thesis: string;
  businessModel: string;
  targetCustomer: string;
  purchaseTrigger: string;
  initialOffer: string;
  distribution: string;
  risks: string[];
  validationPlan: ValidationPlaybookStep[];
  researchSources: Array<{ url: string; title: string; publisher: string; type: string; published_at: string | null; accessed_at: string; supports: string[] }>;
  evidenceLimits: string[];
}

export function isOpportunityAnalysisPayload(value: unknown): value is OpportunityAnalysisPayload {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  const verdict = record.verdict;
  if (!verdict || typeof verdict !== "object" || Array.isArray(verdict)) return false;
  const verdictRecord = verdict as Record<string, unknown>;
  if (!["build", "adapt", "do_not_build_yet"].includes(String(verdictRecord.decision))) return false;
  if (!["high", "medium", "low"].includes(String(verdictRecord.confidence))) return false;
  if (typeof verdictRecord.summary !== "string" || !verdictRecord.summary) return false;
  if (!Array.isArray(verdictRecord.reasons) || verdictRecord.reasons.length < 1 || verdictRecord.reasons.some((reason) => typeof reason !== "string" || !reason)) return false;
  const fields = [
    "problem", "whyNow", "wedge", "coreLoop", "smallestTest", "killCriterion",
    "historicalContext", "thesis", "businessModel", "targetCustomer", "purchaseTrigger",
    "initialOffer", "distribution",
  ] as const;
  return fields.every((field) => typeof record[field] === "string" && Boolean(record[field]))
    && Array.isArray(record.nonGoals)
    && record.nonGoals.length > 0
    && record.nonGoals.every((item) => typeof item === "string" && Boolean(item))
    && Array.isArray(record.risks)
    && record.risks.length >= 3
    && record.risks.every((item) => typeof item === "string" && Boolean(item))
    && Boolean(record.historicalOutcome)
    && typeof record.historicalOutcome === "object"
    && !Array.isArray(record.historicalOutcome)
    && typeof (record.historicalOutcome as Record<string, unknown>).summary === "string"
    && Array.isArray((record.historicalOutcome as Record<string, unknown>).factors)
    && Array.isArray((record.historicalOutcome as Record<string, unknown>).sourceUrls)
    && isValidationPlaybook(record.validationPlan)
    && Array.isArray(record.researchSources)
    && record.researchSources.length >= 3
    && Array.isArray(record.evidenceLimits)
    && record.evidenceLimits.length >= 1;
}

export function publicAnalysis(
  opportunity: ProductCatalogItem["opportunity"],
  locale: Locale,
): OpportunityAnalysisPayload | null {
  if (
    !opportunity?.problem ||
    !opportunity.whyNow ||
    !opportunity.wedge ||
    !opportunity.coreLoop ||
    !opportunity.smallestTest ||
    !opportunity.killCriterion ||
    !opportunity.nonGoals
  ) return null;
  return {
    verdict: opportunity.verdict ? {
      decision: opportunity.verdict.decision,
      confidence: opportunity.verdict.confidence,
      summary: opportunity.verdict.summary[locale],
      reasons: opportunity.verdict.reasons[locale],
    } : {
      decision: "adapt",
      confidence: "low",
      summary: locale === "pt" ? "Ainda não há um veredito público." : "No public verdict is available yet.",
      reasons: [locale === "pt" ? "Abra o dossiê para ver a decisão revisada." : "Open the dossier for the reviewed decision."],
    },
    problem: opportunity.problem[locale],
    whyNow: opportunity.whyNow[locale],
    wedge: opportunity.wedge[locale],
    coreLoop: opportunity.coreLoop[locale],
    smallestTest: opportunity.smallestTest[locale],
    killCriterion: opportunity.killCriterion[locale],
    nonGoals: opportunity.nonGoals[locale],
    historicalContext: opportunity.historicalContext?.[locale] ?? "",
    historicalOutcome: opportunity.historicalOutcome ? {
      disposition: opportunity.historicalOutcome.disposition,
      dispositionConfidence: opportunity.historicalOutcome.dispositionConfidence,
      causalStatus: opportunity.historicalOutcome.causalStatus,
      summary: opportunity.historicalOutcome.summary[locale],
      factors: opportunity.historicalOutcome.factors[locale],
      sourceUrls: opportunity.historicalOutcome.sourceUrls,
    } : {
      disposition: "unknown",
      dispositionConfidence: "unknown",
      causalStatus: "not_publicly_established",
      summary: "",
      factors: [],
      sourceUrls: [],
    },
    thesis: opportunity.opportunityThesis?.[locale] ?? "",
    businessModel: opportunity.businessModel?.[locale] ?? "",
    targetCustomer: opportunity.targetCustomer?.[locale] ?? "",
    purchaseTrigger: opportunity.purchaseTrigger?.[locale] ?? "",
    initialOffer: opportunity.initialOffer?.[locale] ?? "",
    distribution: opportunity.distribution?.[locale] ?? "",
    risks: opportunity.risks?.[locale] ?? [],
    validationPlan: opportunity.validationPlan?.[locale] ?? [],
    researchSources: opportunity.researchSources?.[locale] ?? [],
    evidenceLimits: opportunity.evidenceLimits?.[locale] ?? [],
  };
}
